import { useCallback, useMemo, useState } from 'react';

import { Lock, RefreshCw, UserPlus, Users } from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { GatingBanner } from '@/modulos/portal-credenciado/comum/GatingBanner';
import { PortalAccessGuard } from '@/modulos/portal-credenciado/comum/PortalAccessGuard';
import { usePortalAccess } from '@/modulos/portal-credenciado/comum/usePortalAccess';
import { DataTable } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { StatusBadge } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Textarea } from '@/ui/base/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/base/tooltip';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';

import { EquipeFormulario } from './EquipeFormulario';
import { OperadoresDelegadosCard } from './OperadoresDelegadosCard';
import { RepresentantesLegaisCard } from './RepresentantesLegaisCard';
import { useColaboradores } from './dados';
import type { ColaboradorDoCredenciado, PerfilDeAcessoDoColaborador } from './contrato';

const ROTULO_DO_PERFIL: Record<PerfilDeAcessoDoColaborador, string> = {
  administrador: 'Administrador',
  financeiro: 'Financeiro',
  operador: 'Operador',
};

const FILTROS_PADRAO = { busca: '', perfil: '', status: '' };

const MOTIVO_MINIMO = 5;

/**
 * `/credenciado/equipe` — colaboradores do credenciado com acesso ao Portal
 * do Credenciado. Dado mockado — ver `dados.ts` e `comum/mock.ts` para a
 * justificativa.
 *
 * SÓ QUEM ADMINISTRA O CREDENCIADO COLETIVO GERENCIA A EQUIPE: o titular PJ
 * ou Cooperativa e o administrador convidado por ele (`isAdminCred`, ver
 * `usePortalAccess` — decisão do dono em 2026-10-01). A tela passa pela
 * guarda `requireAdminCred`, igual à rota `/portal/equipe` do protótipo: PF
 * vê "Tela exclusiva de PJ e Cooperativas", e operador ou financeiro vê
 * "Acesso restrito a administradores". O item de menu some para esses perfis
 * (`mapa.ts`, `papeis` e `condicionalPorTipo`), e a rota deixa a guarda para
 * a tela (`guardaNaTela`), senão a tela bloqueada nunca apareceria. Papel
 * interno não entra no Portal nem passa pela guarda. Os botões de adicionar,
 * editar e bloquear/reativar continuam condicionados a quem administra o
 * credenciado (`ehAdmin`), como defesa em profundidade (CLAUDE.md, regra de
 * interface 7).
 *
 * NADA GRAVA, NEM EM MEMÓRIA. Adicionar, editar, bloquear e reativar mostram o
 * aviso de indisponibilidade e não mudam a grade (CLAUDE.md, seção "Proibido":
 * mock só de leitura). O diálogo de bloqueio fica aberto com o motivo digitado.
 *
 * `embutida`: a tela entra como aba de Frota & Equipe, que já tem o `h1` da
 * página; o título desta vira `h2` (`PageHeader nivel={2}`). A rota própria
 * não passa a prop e continua com o `h1`.
 */
export function EquipePage({ embutida = false }: { embutida?: boolean }) {
  return (
    <PortalAccessGuard regra="requireAdminCred">
      <GestaoDaEquipe embutida={embutida} />
    </PortalAccessGuard>
  );
}

function GestaoDaEquipe({ embutida }: { embutida: boolean }) {
  const { isAdminCred: ehAdmin } = usePortalAccess();

  const { data: colaboradores, isLoading, isError, refetch } = useColaboradores();

  const [filtros, setFiltros] = useState<Record<string, string>>(FILTROS_PADRAO);
  const [modalAberto, setModalAberto] = useState(false);
  const [colaboradorEmEdicao, setColaboradorEmEdicao] = useState<ColaboradorDoCredenciado | null>(
    null,
  );
  const [colaboradorParaBloquear, setColaboradorParaBloquear] =
    useState<ColaboradorDoCredenciado | null>(null);
  const [motivoBloqueio, setMotivoBloqueio] = useState('');

  const lista = useMemo(() => {
    const busca = (filtros.busca ?? '').trim().toLowerCase();
    return (colaboradores ?? []).filter((colaborador) => {
      if (
        busca &&
        !`${colaborador.nome} ${colaborador.email} ${colaborador.cargo}`
          .toLowerCase()
          .includes(busca)
      ) {
        return false;
      }
      if (filtros.perfil && colaborador.perfil !== filtros.perfil) {
        return false;
      }
      if (filtros.status === 'ativo' && !colaborador.ativo) {
        return false;
      }
      if (filtros.status === 'bloqueado' && colaborador.ativo) {
        return false;
      }
      return true;
    });
  }, [colaboradores, filtros]);

  const temFiltro = Boolean(filtros.busca || filtros.perfil || filtros.status);

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por nome, e-mail ou cargo…',
      ariaLabel: 'Buscar colaboradores',
      flex: true,
    },
    {
      key: 'perfil',
      label: 'Perfil',
      type: 'select',
      options: (Object.keys(ROTULO_DO_PERFIL) as PerfilDeAcessoDoColaborador[]).map((perfil) => ({
        value: perfil,
        label: ROTULO_DO_PERFIL[perfil],
      })),
      width: 'w-full sm:w-[180px]',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'ativo', label: 'Ativo' },
        { value: 'bloqueado', label: 'Bloqueado' },
      ],
      width: 'w-full sm:w-[150px]',
    },
  ];

  function abrirCadastro() {
    setColaboradorEmEdicao(null);
    setModalAberto(true);
  }

  // Estáveis (useCallback) porque entram nas colunas, e as colunas vão para
  // `useTableViewPreferences`, que pede lista estável.
  const abrirEdicao = useCallback((colaborador: ColaboradorDoCredenciado) => {
    setColaboradorEmEdicao(colaborador);
    setModalAberto(true);
  }, []);

  function fecharModal() {
    setModalAberto(false);
    setColaboradorEmEdicao(null);
  }

  const abrirBloqueio = useCallback((colaborador: ColaboradorDoCredenciado) => {
    setColaboradorParaBloquear(colaborador);
    setMotivoBloqueio('');
  }, []);

  function fecharBloqueio() {
    setColaboradorParaBloquear(null);
    setMotivoBloqueio('');
  }

  function confirmarBloqueio() {
    if (!colaboradorParaBloquear || motivoBloqueio.trim().length < MOTIVO_MINIMO) {
      return;
    }
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  const reativar = useCallback(() => {
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }, []);

  /**
   * O nome identifica a linha e não se oculta; Perfil e Status explicam os
   * filtros de mesmo nome. Ações só existe para quem administra (regra 7) e,
   * quando existe, fica presa no fim.
   */
  const colunas = useMemo<TableViewColumn<ColaboradorDoCredenciado>[]>(
    () => [
      {
        id: 'nome',
        header: 'Nome',
        // Quem administra edita pela linha. Os botões de Ações são controles
        // focáveis dentro dela, então a linha não pode ser também um botão
        // (`nested-interactive`, regras de interface 5 e 9): o nome vira o botão
        // de verdade que abre a edição pelo teclado (é modal, não rota — por isso
        // botão, e não Link). Sem administração, o nome é só texto.
        cell: (colaborador) =>
          ehAdmin ? (
            <button
              type="button"
              onClick={() => abrirEdicao(colaborador)}
              className="focus-visible:ring-ring rounded text-left font-medium hover:underline focus:outline-hidden focus-visible:ring-2"
            >
              {colaborador.nome}
            </button>
          ) : (
            <span className="font-medium">{colaborador.nome}</span>
          ),
        sortKey: 'nome',
        isCritical: true,
      },
      {
        id: 'email',
        header: 'E-mail',
        cell: (colaborador) => (
          <span className="text-sm text-muted-foreground">{colaborador.email}</span>
        ),
        sortKey: 'email',
      },
      {
        id: 'cargo',
        header: 'Cargo',
        cell: (colaborador) => (
          <span className="text-sm text-muted-foreground">{colaborador.cargo || '—'}</span>
        ),
        sortKey: 'cargo',
      },
      {
        id: 'perfil',
        header: 'Perfil',
        cell: (colaborador) => (
          <Badge variant="secondary">{ROTULO_DO_PERFIL[colaborador.perfil]}</Badge>
        ),
        sortKey: 'perfil',
        linkedFilterKeys: ['perfil'],
      },
      {
        id: 'status',
        header: 'Status',
        cell: (colaborador) =>
          colaborador.ativo ? (
            <StatusBadge status="ativo" label="Ativo" />
          ) : (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <StatusBadge status="bloqueado" label="Bloqueado" />
                  </span>
                </TooltipTrigger>
                {colaborador.bloqueado_motivo && (
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    {colaborador.bloqueado_motivo}
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          ),
        sortKey: (colaborador) => (colaborador.ativo ? '1ativo' : '0bloqueado'),
        isStatusColumn: true,
        linkedFilterKeys: ['status'],
      },
      ...(ehAdmin
        ? [
            {
              id: 'acoes',
              header: 'Ações',
              isActionColumn: true,
              cell: (colaborador: ColaboradorDoCredenciado) => {
                if (!colaborador.ativo) {
                  return (
                    <Button variant="ghost" size="sm" data-row-stop onClick={reativar}>
                      <RefreshCw className="h-4 w-4" aria-hidden="true" />
                      Reativar
                    </Button>
                  );
                }

                const ehAdminDoColaborador = colaborador.perfil === 'administrador';

                return (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex" data-row-stop>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={ehAdminDoColaborador}
                            onClick={() => abrirBloqueio(colaborador)}
                          >
                            <Lock className="h-4 w-4" aria-hidden="true" />
                            Bloquear
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {ehAdminDoColaborador && (
                        <TooltipContent side="top" className="max-w-xs text-xs">
                          Administradores da empresa credenciada não podem ser bloqueados por esta
                          tela.
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                );
              },
            } satisfies TableViewColumn<ColaboradorDoCredenciado>,
          ]
        : []),
    ],
    [ehAdmin, abrirEdicao, abrirBloqueio, reativar],
  );

  const filterValues = useMemo(
    () => ({ perfil: filtros.perfil ?? '', status: filtros.status ?? '' }),
    [filtros.perfil, filtros.status],
  );

  const tableView = useTableViewPreferences({
    contextKey: 'credenciado:equipe:list',
    columns: colunas,
    filterValues,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        nivel={embutida ? 2 : 1}
        title="Minha Equipe"
        description="Colaboradores do seu credenciado com acesso ao Portal do Credenciado."
      >
        {/* Abaixo de 640 px o rótulo some (`hidden sm:inline`) e o botão fica
            só com o ícone: o `aria-label` mantém o nome (regra de interface 5). */}
        {ehAdmin && (
          <Button onClick={abrirCadastro} aria-label="Adicionar colaborador">
            <UserPlus className="h-4 w-4 sm:mr-2" aria-hidden="true" />
            <span className="hidden sm:inline">Adicionar colaborador</span>
          </Button>
        )}
      </PageHeader>

      <GatingBanner />

      {ehAdmin && (
        <ResponsiveModal
          open={modalAberto}
          onOpenChange={(aberto) => (aberto ? setModalAberto(true) : fecharModal())}
          title={colaboradorEmEdicao ? 'Editar colaborador' : 'Adicionar colaborador'}
          description="Defina o perfil de acesso que este colaborador vai ter no Portal do Credenciado."
          desktopMaxWidth="max-w-lg"
        >
          <EquipeFormulario
            colaborador={colaboradorEmEdicao ?? undefined}
            onCancelar={fecharModal}
          />
        </ResponsiveModal>
      )}

      {ehAdmin && (
        <ResponsiveModal
          open={!!colaboradorParaBloquear}
          onOpenChange={(aberto) => (aberto ? undefined : fecharBloqueio())}
          title="Bloquear colaborador"
          description={
            colaboradorParaBloquear
              ? `Bloquear o acesso de ${colaboradorParaBloquear.nome}? O colaborador deixa de conseguir entrar no portal até ser reativado.`
              : undefined
          }
          desktopMaxWidth="max-w-md"
          footer={
            <>
              <Button type="button" variant="outline" onClick={fecharBloqueio}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={confirmarBloqueio}
                disabled={motivoBloqueio.trim().length < MOTIVO_MINIMO}
              >
                Bloquear
              </Button>
            </>
          }
        >
          <div className="space-y-1.5">
            <label htmlFor="motivo-bloqueio" className="text-sm font-medium">
              Motivo do bloqueio *
            </label>
            <Textarea
              id="motivo-bloqueio"
              value={motivoBloqueio}
              onChange={(evento) => setMotivoBloqueio(evento.target.value)}
              placeholder="Ex.: desligamento, troca de função, suspeita de uso indevido…"
              rows={3}
            />
            {motivoBloqueio.trim().length > 0 && motivoBloqueio.trim().length < MOTIVO_MINIMO && (
              <p className="text-xs text-destructive-text">
                O motivo precisa ter pelo menos {MOTIVO_MINIMO} caracteres.
              </p>
            )}
          </div>
        </ResponsiveModal>
      )}

      <FilterBar
        fields={campos}
        values={filtros}
        onChange={(chave, valor) => setFiltros((atual) => ({ ...atual, [chave]: valor }))}
        onClear={() => setFiltros(FILTROS_PADRAO)}
        defaultValues={FILTROS_PADRAO}
      />

      {isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar sua equipe agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={tableView.orderedColumns}
          personalizacao={tableView}
          data={lista}
          isLoading={isLoading}
          onRowClick={ehAdmin ? abrirEdicao : undefined}
          linhaComElementoInterativo={ehAdmin}
          emptyIcon={Users}
          emptyTitle="Nenhum colaborador cadastrado"
          emptyDescription={
            temFiltro
              ? 'Nenhum colaborador atende a esses filtros. Tente afrouxar a busca.'
              : ehAdmin
                ? 'Adicione colaboradores para que eles acessem o portal em nome do seu credenciado.'
                : 'Quando houver colaborador com acesso ao portal, ele aparece aqui.'
          }
          minWidth="700px"
          ariaLabel="Lista de colaboradores"
        />
      )}

      <OperadoresDelegadosCard podeGerenciar={ehAdmin} />

      <RepresentantesLegaisCard podeGerenciar={ehAdmin} />
    </div>
  );
}

export default EquipePage;
